import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import {
  DEFAULT_COUNTRY_CODE,
  signUpSchema,
  type SignUpInput,
  type SignUpValues,
} from '@sf/shared';
import { Button } from '../../components/ui/Button';
import { PasswordField, PhoneField, TextField } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { signUp } from './api';

export function SignUpPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignUpInput, unknown, SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { countryCode: DEFAULT_COUNTRY_CODE },
    mode: 'onTouched',
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await signUp(values);
      toast({ title: t('auth.signup.success'), variant: 'success' });
      navigate('/awaiting-approval', { replace: true });
    } catch (err) {
      const message = messageForError(err);
      if ((err as { code?: string }).code === 'auth/email-already-in-use')
        setError('email', { message });
      else toast({ title: message, variant: 'error' });
    }
  });

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('auth.signup.title')}</h1>
      <p className="mt-1 text-sm text-slate">
        Register with your unit number. Management will confirm your details before you can sign in
        fully.
      </p>
      <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
        <TextField
          label="Full name"
          autoComplete="name"
          required
          error={errors.name?.message}
          {...register('name')}
        />
        <PhoneField
          label="Phone number"
          required
          codeInput={register('countryCode')}
          numberInput={register('phone')}
          codeError={errors.countryCode?.message}
          numberError={errors.phone?.message}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <PasswordField
          label="Password"
          autoComplete="new-password"
          required
          hint="At least 8 characters."
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordField
          label="Confirm password"
          autoComplete="new-password"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <TextField
          label="Unit number"
          autoComplete="off"
          required
          hint="For example 101, A-101 or B1203."
          error={errors.unitNumber?.message}
          {...register('unitNumber')}
        />
        <Button type="submit" loading={isSubmitting} className="mt-2 w-full justify-center">
          Create account
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate">
        Already registered?{' '}
        <Link to="/login" className="font-medium text-brand underline-offset-2 hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  );
}
